import {
  PROPERTY_DOCUMENT_CONTENT_TYPES,
  PROPERTY_DOCUMENT_KINDS,
  PROPERTY_DOCUMENT_MAX_BYTES,
  PROPERTY_PROFILE_GENERAL_DOCUMENT_SLOTS,
  type PropertyDocumentKind,
} from '@natalan/capehost-contracts'
import { PROFILE_SECTIONS } from '@/lib/profile'

/**
 * A label per document kind, from the slot declarations both UIs render
 * (the section slots and the general "Photos and documents" area), so
 * nothing is retyped here. A kind no slot names falls back to its id.
 */
export const DOCUMENT_KIND_LABELS: Record<PropertyDocumentKind, string> = Object.fromEntries(
  PROPERTY_DOCUMENT_KINDS.map(kind => {
    const slot =
      PROPERTY_PROFILE_GENERAL_DOCUMENT_SLOTS.find(s => s.kind === kind) ??
      PROFILE_SECTIONS.flatMap(section => section.document_slots).find(s => s.kind === kind)
    return [kind, slot?.label ?? kind]
  })
) as Record<PropertyDocumentKind, string>

/** "2.4 MB", "830 KB". */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

/**
 * The checks admin-api makes, run first for a quicker answer; admin-api still
 * decides (it sniffs the type from the bytes). The browser's type can be
 * empty for HEIC, so an empty type is left to the server.
 */
export function uploadProblem(file: File): string | null {
  if (file.size === 0) return 'This file is empty. Please choose another file.'
  if (file.size > PROPERTY_DOCUMENT_MAX_BYTES) {
    return 'This file is larger than 25 MB. Please upload a smaller file or a compressed copy.'
  }
  // Only a type that is plainly neither a PDF nor an image is refused here;
  // admin-api sniffs the bytes, so a HEIF reported as image/heif gets there.
  if (
    file.type &&
    !(PROPERTY_DOCUMENT_CONTENT_TYPES as readonly string[]).includes(file.type) &&
    !file.type.startsWith('image/')
  ) {
    return 'This file type is not supported. Please upload a PDF or a JPEG, PNG, HEIC or WEBP image.'
  }
  return null
}
